import React, { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { EonUiIcon } from "@eon-ui/eon-ui-components-react";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import { useDebouncedCallback } from "@me8eon/debounce";
import { useTranslation } from "@me8eon/translation";
import { fetchCategoryQueries, DashboardCategoryQuery, DashboardRange, CategoryQuerySort } from "../../../services/dashboardApi";
import { fmt, currentLocale } from "../poData";

type Props = {
    parent: string;
    child: string;
    label: string;
    range: DashboardRange;
    companies?: string[];
    search?: string;
    onClose: () => void;
};

const PAGE = 50;
// Toggling the company filter while this modal is open fires one state change per click —
// debounce the actual refetch so a burst of clicks collapses into one request.
const FILTER_DEBOUNCE_MS = 350;

// Clicking the Sentiment header cycles through these three, always starting at the first when
// switching over from the Date header (or from the unsorted default).
const SENTIMENT_SORTS: CategoryQuerySort[] = ["likes-first", "dislikes-first", "no-feedback-first"];
const isSentimentSort = (s: CategoryQuerySort | null): s is CategoryQuerySort => !!s && SENTIMENT_SORTS.includes(s);
const isDateSort = (s: CategoryQuerySort | null): s is CategoryQuerySort => s === "date-asc" || s === "date-desc";

const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(currentLocale(), { year: "numeric", month: "short", day: "numeric" });
};

/** Drill-down modal: lists the individual queries for one category, newest-first, paginated. */
const CategoryQueriesPanel: React.FC<Props> = ({ parent, child, label, range, companies, search, onClose }) => {
    const t = useTranslation();
    const tp = (key: string) => t(`productDashboard.categoryQueries.${key}`);
    const sentimentBadge = (s: string | null) => {
        if (s === "like")
            return (
                <span className="po-sentiment-badge po-sentiment-badge--like">
                    <EonUiIcon size="small" name="thumb_up" className="po-thumb po-thumb--up" />
                    {tp("like")}
                </span>
            );
        if (s === "dislike")
            return (
                <span className="po-sentiment-badge po-sentiment-badge--dislike">
                    <EonUiIcon size="small" name="thumb_up" className="po-thumb po-thumb--down" />
                    {tp("dislike")}
                </span>
            );
        return <span className="po-sentiment-badge">—</span>;
    };
    const idToken = useJwtToken();
    // `idToken` is a fn — always truthy once useJwtToken() returns, even before the user is
    // actually authenticated, so it can't signal "auth is ready". UserData.loggedIn can.
    const { loggedIn } = useUserData();
    const [items, setItems] = useState<DashboardCategoryQuery[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [downloading, setDownloading] = useState(false);
    // Active column sort (Sentiment/Date header click) — null = default (newest-first).
    const [sort, setSort] = useState<CategoryQuerySort | null>(null);
    const toggleDateSort = () => setSort((prev) => (prev === "date-desc" ? "date-asc" : "date-desc"));
    const toggleSentimentSort = () =>
        setSort((prev) => {
            const idx = prev ? SENTIMENT_SORTS.indexOf(prev) : -1;
            return SENTIMENT_SORTS[(idx + 1) % SENTIMENT_SORTS.length];
        });

    // Close on Esc + lock body scroll while the modal is open (mount/unmount only).
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onCloseRef.current();
        };
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, []);

    // (Re)load the first page whenever the selected category (or range/company filter) changes.
    // The refetch is debounced (FILTER_DEBOUNCE_MS) for changes AFTER the panel opens; the first
    // load (mount, or once actually logged in) runs immediately via a separate, non-debounced
    // effect below, so loggedIn/idToken never drive (or reset) the debounce itself.
    const rangeKey = "from" in range ? `r:${range.from}:${range.to}` : `w:${range.window}`;
    const companiesKey = (companies ?? []).join(",");
    const latestRef = useRef({ range, idToken, companies, search, sort, loggedIn });
    latestRef.current = { range, idToken, companies, search, sort, loggedIn };
    const requestIdRef = useRef(0);
    const mountedRef = useRef(true);
    useEffect(() => {
        // Reset on setup too (not just rely on the initial useRef value) — React StrictMode's dev
        // double-invoke runs setup -> cleanup -> setup again for the same mount, so without this
        // the cleanup below would leave mountedRef stuck at false even though still mounted.
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    // Key of last params we fetched — null until the first fetch.
    const lastFetchKeyRef = useRef<string | null>(null);

    const runFetch = useCallback(() => {
        const { range, idToken, companies, search, sort, loggedIn } = latestRef.current;
        if (!loggedIn) return; // not actually authenticated yet — idToken() would just fail
        const requestId = ++requestIdRef.current;
        // setItems([]);
        // Keep the previously-loaded rows on screen until the new page actually arrives — clearing
        // them eagerly here made the modal visibly collapse then re-expand on every sort/search
        // change (table briefly empty, then repopulated a moment later).
        fetchCategoryQueries(range, parent, child, idToken, PAGE, 0, companies, search, sort ?? undefined)
            .then((r) => {
                if (!mountedRef.current || requestIdRef.current !== requestId) return; // superseded or unmounted
                setItems(r.items);
                setTotal(r.total);
            })
            .catch(() => mountedRef.current && requestIdRef.current === requestId && setError(true))
            .finally(() => mountedRef.current && requestIdRef.current === requestId && setLoading(false));
        // loggedIn deliberately excluded from this callback's OWN deps — runFetch reads the live
        // value from latestRef, and auth readiness is handled by the effect below.
    }, [parent, child, rangeKey, companiesKey, search, sort]);

    const debouncedFetch = useDebouncedCallback(runFetch, FILTER_DEBOUNCE_MS);

    // Single effect for all fetches (immediate first load, then debounced on change) — splitting
    // this into two effects previously double-fired on mount, since both ran in the same commit.
    useEffect(() => {
        if (!loggedIn) return;
        const key = `${parent}|${child}|${rangeKey}|${companiesKey}|${search ?? ""}|${sort ?? ""}`;
        if (lastFetchKeyRef.current === key) return; // already fetched/in-flight
        const isFirstLoad = lastFetchKeyRef.current === null;
        lastFetchKeyRef.current = key;
        setLoading(true);
        setError(false);
        if (isFirstLoad) runFetch();
        else debouncedFetch();
    }, [loggedIn, parent, child, rangeKey, companiesKey, search, sort, runFetch, debouncedFetch]);

    const loadMore = () => {
        setLoading(true);
        fetchCategoryQueries(range, parent, child, idToken, PAGE, items.length, companies, search, sort ?? undefined)
            .then((r) => {
                setItems((prev) => [...prev, ...r.items]);
                setTotal(r.total);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    };

    // Download the FULL category list (not just the loaded page) as a real .xlsx.
    const handleDownload = async () => {
        setDownloading(true);
        try {
            const DL_PAGE = 200; // backend cap per request; loop until we have them all
            const all: DashboardCategoryQuery[] = [];
            let tot = Infinity;
            for (let off = 0; all.length < tot; off += DL_PAGE) {
                const r = await fetchCategoryQueries(range, parent, child, idToken, DL_PAGE, off, companies, search, sort ?? undefined);
                tot = r.total;
                all.push(...r.items);
                if (r.items.length === 0) break; // safety against an unexpected empty page
            }
            const header = [tp("colQuery"), tp("colSentiment"), tp("colDate"), tp("colSource"), tp("colLanguage"), tp("colCompany")];
            const rows = all.map((q) => [q.text ?? "", q.sentiment ?? "", fmtDate(q.timestamp), q.source ?? "", q.language ?? "", q.company ?? ""]);
            const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Queries");
            const safe = label.trim().replace(/[^a-zA-Z0-9-_]+/g, "_").replace(/^_+|_+$/g, "").toLowerCase() || "category";
            XLSX.writeFile(wb, `queries-${safe}.xlsx`);
        } catch {
            setError(true);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="po-modal" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="po-modal__dialog" onClick={(e) => e.stopPropagation()}>
                <div className="po-modal__head">
                    <div>
                        <h3>{tp("titlePrefix")} {label}</h3>
                        <p className="po-modal__sub">
                            {error ? tp("loadError") : `${fmt(total)} ${total === 1 ? tp("query") : tp("queries")}`}
                        </p>
                    </div>
                    <div className="po-modal__head-actions">
                        <button
                            type="button"
                            className="po-download-btn"
                            onClick={handleDownload}
                            disabled={downloading || error || total === 0}
                        >
                            {downloading ? tp("preparing") : tp("downloadExcel")}
                        </button>
                        <button type="button" className="po-modal__close" onClick={onClose} aria-label={tp("closeAria")}>
                            ✕
                        </button>
                    </div>
                </div>

                <div className="po-modal__body">
                    {!error && (
                        <table className="po-table">
                            <thead>
                                <tr>
                                    <th>{tp("thQuery")}</th>
                                    <th
                                        className={`po-table__sortable ${isSentimentSort(sort) ? "po-table__sortable--active" : ""}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={toggleSentimentSort}
                                        onKeyDown={(e) => e.key === "Enter" && toggleSentimentSort()}
                                        title={tp("sortSentimentHint")}
                                    >
                                        {tp("thSentiment")}
                                    </th>
                                    <th
                                        className={`po-table__sortable ${isDateSort(sort) ? "po-table__sortable--active" : ""}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={toggleDateSort}
                                        onKeyDown={(e) => e.key === "Enter" && toggleDateSort()}
                                        title={tp("sortDateHint")}
                                    >
                                        <span className="po-table__sort-label">
                                            {tp("thDate")}
                                            {isDateSort(sort) && <span className="po-table__sort-icon">{sort === "date-asc" ? "\u2191" : "\u2193"}</span>}
                                        </span>
                                    </th>
                                    <th>{tp("thLanguage")}</th>
                                    <th>{tp("thCompany")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((q, i) => (
                                    <tr key={i}>
                                        <td className="po-table__strong">{q.text || "—"}</td>
                                        <td>{sentimentBadge(q.sentiment)}</td>
                                        <td className="po-table__muted">{fmtDate(q.timestamp)}</td>
                                        <td className="po-table__muted">{q.language || "—"}</td>
                                        <td className="po-table__muted">{q.company || "—"}</td>
                                    </tr>
                                ))}
                                {!loading && items.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="po-table__muted">
                                            {tp("empty")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {loading && <p className="po-modal__sub" style={{ padding: "12px 4px" }}>{tp("loading")}</p>}

                    {!loading && !error && items.length < total && (
                        <button type="button" className="po-link" style={{ marginTop: 12 }} onClick={loadMore}>
                            {tp("loadMorePrefix")} {fmt(items.length)} {tp("of")} {fmt(total)}) →
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CategoryQueriesPanel;
